import { Link } from 'react-router-dom';

const TradeInValueFactors = () => {
  const factors = [
    {
      icon: 'ri-calendar-line',
      name: 'Year & Age',
      description: 'Newer vehicles hold value better. Depreciation accelerates after 5 years.',
      impact: 'High'
    },
    {
      icon: 'ri-dashboard-line',
      name: 'Mileage',
      description: 'Lower miles mean less wear. Average is 12-15k per year.',
      impact: 'High'
    },
    {
      icon: 'ri-paint-brush-line',
      name: 'Condition & Cosmetics',
      description: 'Dents, scratches, interior wear all reduce value. Clean cars sell faster.',
      impact: 'Medium'
    },
    {
      icon: 'ri-file-damage-line',
      name: 'Accident & Title History',
      description: 'Clean title is crucial. Even minor accidents can drop value 10-20%.',
      impact: 'High'
    },
    {
      icon: 'ri-line-chart-line',
      name: 'Market Demand',
      description: 'Popular makes/models hold value. Trucks and SUVs typically stronger than sedans.',
      impact: 'High'
    },
    {
      icon: 'ri-settings-3-line',
      name: 'Trim Level & Options',
      description: 'Higher trims and desirable features (leather, sunroof, AWD) add value.',
      impact: 'Medium'
    },
    {
      icon: 'ri-tools-line',
      name: 'Mechanical Health',
      description: 'Service records matter. Known issues (transmission, engine) hurt value significantly.',
      impact: 'High'
    },
    {
      icon: 'ri-sun-line',
      name: 'Time of Year',
      description: 'Convertibles sell better in spring. 4WD trucks peak in fall/winter.',
      impact: 'Low'
    }
  ];

  const dirtyTricks = [
    {
      icon: 'ri-shuffle-line',
      trick: 'The Bundle Game',
      description: 'They mix your trade value into the purchase price negotiation so you can\'t tell if you\'re getting a fair deal on either.'
    },
    {
      icon: 'ri-tools-fill',
      trick: 'Phantom Reconditioning Costs',
      description: '"We have to detail it, fix that scratch, replace the tires..." — costs they\'d do anyway, used to justify lowballing you.'
    },
    {
      icon: 'ri-book-2-line',
      trick: 'Selective "Book Value" Citing',
      description: 'They quote KBB trade-in value (lowest) when buying yours, then cite retail value (highest) when selling theirs.'
    }
  ];

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-amber-100 text-amber-700';
      case 'Low':
        return 'bg-[#F5C842]/20 text-[#92700a]';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-1 bg-[#F5C842]/20 text-[#92700a] rounded-full text-sm font-medium mb-4">
            Trade-In Education
          </div>
          <h2 className="text-3xl font-bold text-[#1A2332] mb-4">
            What Affects Your Trade-In Value
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Understanding these factors helps you know what to expect — and what dealers might try to use against you.
          </p>
        </div>

        {/* Factors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {factors.map((factor, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#1A2332]/8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(26,35,50,0.08)' }}>
                  <i className={`${factor.icon} text-2xl text-[#1A2332]`}></i>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getImpactColor(factor.impact)}`}>
                  {factor.impact} Impact
                </span>
              </div>
              <h3 className="text-lg font-bold text-[#1A2332] mb-2">
                {factor.name}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {factor.description}
              </p>
            </div>
          ))}
        </div>

        {/* Dealer Dirty Tricks Callout */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 border-2 border-red-200 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
              <i className="ri-alert-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[#1A2332]">
                Dealer Dirty Tricks to Watch For
              </h3>
              <p className="text-gray-600">
                These tactics are designed to confuse you and lower your trade value
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {dirtyTricks.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-xl text-red-600`}></i>
                </div>
                <h4 className="text-lg font-bold text-[#1A2332] mb-2">
                  {item.trick}
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-white rounded-lg border-2 border-red-300">
            <p className="text-sm text-gray-700">
              <strong className="text-red-600">Our approach:</strong> We give you a straight number based on real market data — no games, no bundling, no phantom fees. What you see is what you get.
            </p>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <a
            href="#trade-in-form"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#1A2332] text-[#F5C842] font-bold rounded-lg hover:bg-[#243044] transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className="ri-calculator-line text-xl"></i>
            Now That You Know What Matters — Get Your Real Number
          </a>
          <p className="text-sm text-gray-500 mt-4">
            No games. No lowballing. Just honest appraisals.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TradeInValueFactors;
